<script setup lang="ts">
import { computed, type PropType } from 'vue';
import type { VaultFileRevisionInDb } from '@/api';
import { formatDateForDisplay } from '@/utils/datetimeUtils';
import GaiaAddButton from '../../base/GaiaAddButton.vue';
import GaiaSectionHeader from '../../base/GaiaSectionHeader.vue';
import GaiaTag from '../../base/GaiaTag.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaDataTable, { type GaiaDataColumn } from '../../composite/GaiaDataTable.vue';
import GaiaContractStateCell from './GaiaContractStateCell.vue';
import { gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  /** Newest first, as the vault lists them. */
  revisions: {
    type: Array as PropType<VaultFileRevisionInDb[]>,
    required: true,
  },
  /** The file's current revision. */
  currentUid: {
    type: String as PropType<string | null>,
    default: null,
  },
  /** The revision the page is reading. */
  selectedUid: {
    type: String as PropType<string | null>,
    default: null,
  },
  /** Extracted contract-type fields per revision uid; missing while not counted. */
  fieldCounts: {
    type: Object as PropType<Record<string, number>>,
    default: () => ({}),
  },
  /** Appendices take no part in the contract's state, so they show none. */
  hasState: {
    type: Boolean,
    default: true,
  },
  /** Why the caller can't add a version; null when they can. */
  writeBlock: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{ select: [uid: string]; add: [] }>();

const columns = computed<GaiaDataColumn[]>(() => [
  { key: 'version', label: 'Version', width: 'minmax(220px,1fr)' },
  ...(props.hasState ? [{ key: 'state', label: 'State', width: '200px' }] : []),
  { key: 'fields', label: 'Fields', width: '110px' },
  { key: 'created', label: 'Created', width: '130px' },
]);

const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
const selectedBg = gaiaNeutral.surfaceTint;
</script>

<template>
  <!-- The Files section of the Contract View mock, read as this file's
       versions: one row per revision, the one being read highlighted. A row
       opens that version; the page announces the preview. -->
  <n-flex vertical :size="10">
    <!-- Label left, Add right, on one centre line — #extra would set the
         button inline after the label, where it sits on the text's baseline. -->
    <n-flex align="center" justify="space-between" :size="12" :wrap="false">
      <gaia-section-header>Versions</gaia-section-header>
      <gaia-add-button
        subject="version"
        :disabled="Boolean(props.writeBlock)"
        :title="props.writeBlock ?? ''"
        @click="emit('add')"
      />
    </n-flex>

    <gaia-data-table :columns="columns" :action-width="0">
      <template #default="{ rowStyle }">
        <div
          v-for="revision in props.revisions"
          :key="revision.uid"
          class="row"
          :class="{ selected: revision.uid === props.selectedUid }"
          :style="rowStyle"
          @click="emit('select', revision.uid)"
        >
          <n-flex vertical :size="2" :style="{ minWidth: 0 }">
            <n-flex align="center" :size="8" :wrap="false">
              <gaia-text size="row" weight="semibold" tone="ink">
                v{{ revision.version_number }}
              </gaia-text>
              <gaia-tag v-if="revision.uid === props.currentUid" size="small" variant="muted">
                Current
              </gaia-tag>
            </n-flex>
            <gaia-text size="meta" tone="tertiary" truncate :title="revision.file_name">
              {{ revision.change_description || revision.file_name }}
            </gaia-text>
          </n-flex>
          <gaia-contract-state-cell
            v-if="props.hasState"
            compact
            :state="revision.effective_state"
            :reasons="revision.needs_attention_reasons"
            :failure-reason="revision.signature_failure_reason ?? null"
            :signature-infos="revision.signature_infos ?? null"
          />
          <gaia-text size="dense" tone="soft">
            {{
              props.fieldCounts[revision.uid] === undefined
                ? '—'
                : `${props.fieldCounts[revision.uid]} extracted`
            }}
          </gaia-text>
          <gaia-text size="dense" tone="soft">{{
            formatDateForDisplay(revision.created_at)
          }}</gaia-text>
        </div>
      </template>
    </gaia-data-table>
  </n-flex>
</template>

<style scoped>
.row {
  display: grid;
  align-items: center;
  gap: 16px;
  padding: 12px 18px;
  border-top: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

.row.selected {
  background: v-bind(selectedBg);
}
</style>
